"use client";

import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { useRef, useState } from "react";
import type { HeroContent } from "@/src/data/home";
import { useHeroParallaxVars } from "@/src/hooks/useHeroParallaxVars";

type SceneCardProps = {
  src: string;
  label: string;
  className: string;
  kicker: string;
  tone: "mint" | "cyan" | "amber";
  priority?: boolean;
};

function SceneCard({
  src,
  label,
  className,
  kicker,
  tone,
  priority = false,
}: SceneCardProps) {
  const [failed, setFailed] = useState(false);

  return (
    <article className={`hero-float-card ${className}`}>
      <span
        className={`hero-float-card__aura hero-float-card__aura--${tone}`}
      />
      <div className="hero-float-card__media">
        {failed ? (
          <div className="hero-float-card__fallback">
            <span>{label}</span>
          </div>
        ) : (
          <img
            src={src}
            alt=""
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            className="hero-float-card__img"
            onError={() => setFailed(true)}
          />
        )}
      </div>
      <div className="hero-float-card__caption">
        <p className="hero-float-card__kicker">{kicker}</p>
        <p className="hero-float-card__title">{label}</p>
      </div>
    </article>
  );
}

type HeroStageProps = {
  content: HeroContent;
};

export function HeroStage({ content }: HeroStageProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const { reducedMotion } = useHeroParallaxVars(stageRef);

  return (
    <section
      className="grid gap-10 pb-3 pt-2 lg:grid-cols-[1.08fr_1fr] lg:items-center"
      aria-labelledby="home-hero-title"
    >
      <div data-reveal={true}>
        <span className="section-kicker">
          <Sparkles className="h-4 w-4" />
          {content.badge}
        </span>
        <h1
          id="home-hero-title"
          className="mt-5 max-w-[18ch] font-heading text-[length:var(--eg-type-display)] font-semibold leading-[1.02] text-slate-900"
        >
          {content.title}
        </h1>
        <p className="mt-4 max-w-xl text-[0.98rem] leading-relaxed text-slate-600 sm:text-base">
          {content.subtitle}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={content.primaryCta.href}
            className="home-focus cta-primary-shimmer inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-6 py-3 text-sm font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(115,255,194,0.28)]"
          >
            {content.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={content.secondaryCta.href}
            className="home-focus inline-flex items-center gap-2 rounded-xl border border-cyan-500/35 bg-cyan-100/74 px-6 py-3 text-sm font-semibold text-cyan-900 transition hover:-translate-y-0.5 hover:border-cyan-500/55 hover:bg-cyan-100"
          >
            {content.secondaryCta.label}
          </a>
        </div>

        <ul className="mt-7 grid gap-2 sm:grid-cols-2">
          {content.trustChips.map((chip) => (
            <li
              key={chip}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300/70 bg-white/72 px-3 py-2 text-xs text-slate-700"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              {chip}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-3" data-reveal={true}>
        <div className="home-stage-frame">
          <div ref={stageRef} className="hero-immersive" aria-hidden={true}>
            <div className="hero-immersive__viewport">
              <div className="hero-ambient hero-ambient--mint" />
              <div className="hero-ambient hero-ambient--cyan" />
              <div className="hero-ambient hero-ambient--amber" />
              <div className="hero-depth-grid" />
              <div className="hero-orbit hero-orbit--outer" />
              <div className="hero-orbit hero-orbit--inner" />
              <div className="hero-scan-beam" />

              <SceneCard
                src="/hero/vape.png"
                label="Vaporizer-Stift"
                kicker="Neu im Sortiment"
                tone="amber"
                className="hero-float-card--vape"
                priority={true}
              />
              <SceneCard
                src="/hero/grinder.png"
                label="Aluminium-Grinder"
                kicker="Beliebtes Zubehör"
                tone="mint"
                className="hero-float-card--grinder"
              />
              <SceneCard
                src="/hero/glass.png"
                label="Glasbong"
                kicker="Klassiker im Laden"
                tone="cyan"
                className="hero-float-card--glass"
              />

              <div className="hero-ground-glow" />
              <div className="hero-specular" />
            </div>
          </div>
          <div className="hero-stage__meta">
            <span>
              {reducedMotion
                ? "Ausgewählte Highlights aus dem Laden Gummersbach"
                : "Aktuelle Highlights aus dem Laden Gummersbach"}
            </span>
            <span className="hero-stage__meta-pill">
              Moltkestraße 8 · 51643 Gummersbach
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
